import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/hooks/useAuth";
import type { Booking, BookingWithVehicle, Vehicle } from "@/types";

export function useHostDashboard() {
  const { user } = useAuth();
  const [listings, setListings] = useState<Vehicle[]>([]);
  const [requests, setRequests] = useState<BookingWithVehicle[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!user) {
      setListings([]);
      setRequests([]);
      setLoading(false);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const [vehicleRes, bookingRes] = await Promise.all([
        supabase.from("vehicles").select("*").eq("owner_id", user.id).order("created_at", { ascending: false }),
        supabase
          .from("bookings")
          .select("*")
          .eq("owner_id", user.id)
          .order("created_at", { ascending: false }),
      ]);

      if (vehicleRes.error) throw vehicleRes.error;
      if (bookingRes.error) throw bookingRes.error;

      const ownedVehicles = (vehicleRes.data as Vehicle[]) ?? [];
      const rows = (bookingRes.data as Booking[]) ?? [];
      const vehicleMap = new Map(ownedVehicles.map((vehicle) => [vehicle.id, vehicle]));

      // Some bookings may be for vehicles not in the fetched list — resolve them too.
      const missingIds = rows
        .map((row) => row.vehicle_id)
        .filter((id): id is number => typeof id === "number" && !vehicleMap.has(id));
      if (missingIds.length > 0) {
        const { data: extra } = await supabase
          .from("vehicles")
          .select("*")
          .in("id", Array.from(new Set(missingIds)));
        ((extra as Vehicle[]) ?? []).forEach((vehicle) => vehicleMap.set(vehicle.id, vehicle));
      }

      setListings(ownedVehicles);
      setRequests(
        rows.map((row) => ({
          ...row,
          vehicle: row.vehicle_id ? vehicleMap.get(row.vehicle_id) ?? null : null,
        }))
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : "We couldn't load your dashboard right now.");
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    load();
  }, [load]);

  const updateBookingStatus = useCallback(
    async (bookingId: number, status: string) => {
      const { error: updateError } = await supabase.from("bookings").update({ status }).eq("id", bookingId);
      if (updateError) throw updateError;
      setRequests((prev) => prev.map((item) => (item.id === bookingId ? { ...item, status } : item)));
    },
    []
  );

  const toggleListingAvailability = useCallback(
    async (vehicleId: number, available: boolean) => {
      const { error: updateError } = await supabase.from("vehicles").update({ available }).eq("id", vehicleId);
      if (updateError) throw updateError;
      setListings((prev) => prev.map((item) => (item.id === vehicleId ? { ...item, available } : item)));
    },
    []
  );

  return {
    listings,
    requests,
    loading,
    error,
    reload: load,
    updateBookingStatus,
    toggleListingAvailability,
  };
}